import { useState, useEffect, useRef } from 'react'
import { useMutation } from "@apollo/client/react";
import { useLocation } from 'react-router'
import { enqueueSnackbar } from 'notistack'
import {
  filter,
  toLower,
  includes,
  isEmpty,
  slice,
  find,
  compact,
  map,
} from '@/utils/lodash'
import { GENERIC_SEND_EMAIL } from '@/housefolios-components/Session/mutations'
import useAffiliate from '@/hooks/useAffiliate'
import useProperties, { KindOfPage } from '@/hooks/useProperties'

/**
 * Custom hook for public home page state and data management
 */
export const usePublicHome = (props) => {
  const { marketplace, orgName } = props
  const location = useLocation()
  const contactUs = useRef(null)

  const [name, setName] = useState()
  const [email, setEmail] = useState()
  const [phone, setPhone] = useState()
  const [message, setMessage] = useState()

  const [genericSendEmail] = useMutation(GENERIC_SEND_EMAIL)
  const [{ affiliate, urlAffiliate }] = useAffiliate({})

  const [
    {
      properties,
      count,
      currentPageProperties,
      marketPlaceData,
      loadingMarketPlaceData,
      marketPlaceError,
      ordering,
      page,
      selected,
      search,
      marketplaceSelected,
    },
    {
      selectAll,
      selectProperty,
      getSearchResults,
      getFilteredProperties,
      fetchMoreProperties,
      setOrdering,
      selectMarketplaceProperty,
      setMarketplaceSelected,
      handlePageChange,
      handleRequestSort,
      setSelected,
      setPage,
    },
  ] = useProperties({
    kind: KindOfPage.PUBLIC_MARKETPLACE,
    marketplace: marketplace?.url,
  })

  // Filter properties based on expiration date and sold status
  let unfilteredProperties = !marketplace
    ? filter(
        marketPlaceData?.marketplaceProperties?.properties,
        (property) =>
          new Date(property?.marketplaces?.expirationDate).getTime() >
          Date.now(),
      )
    : marketPlaceData?.marketplaceProperties?.properties

  unfilteredProperties = props.sold
    ? filter(
        unfilteredProperties,
        (property) => toLower(property?.status || '') === 'sold',
      )
    : filter(
        unfilteredProperties,
        (property) => toLower(property?.status || '') !== 'sold',
      )

  // Get featured properties
  let displayProperties =
    marketplace &&
    compact(
      map(marketplace.featuredProperties, (id) =>
        find(
          marketPlaceData?.marketplaceProperties?.properties,
          (property) => property._id === id,
        ),
      ),
    )

  if (isEmpty(displayProperties)) {
    displayProperties = slice(unfilteredProperties, 0, 6)
  }

  // Get marketplace video URL
  let marketplaceVideoURL = marketplace
    ? includes(marketplace.marketplaceVideoURL, 'http://') ||
      includes(marketplace.marketplaceVideoURL, 'https://')
      ? marketplace.marketplaceVideoURL || ''
      : ''
    : ''

  // Scroll to contact section
  useEffect(() => {
    if (location.hash && contactUs.current) {
      contactUs.current.scrollIntoView({ behavior: 'smooth'  })
    }
  }, [location])

  // Header scroll effect
  useEffect(() => {
    const resizeHeaderOnScroll = () => {
      const distanceY =
          window.pageYOffset || document.documentElement.scrollTop,
        shrinkOn = 200,
        headerEl = document.getElementById('js-header')

      if (distanceY > shrinkOn) {
        headerEl.classList.add('smaller')
      } else {
        headerEl.classList.remove('smaller')
      }
    }

    window.addEventListener('scroll', resizeHeaderOnScroll)
    return () =>
      window.removeEventListener('scroll', resizeHeaderOnScroll)
  }, [])

  const sendEmail = () => {
    const hostName = import.meta.env.VITE_HOSTNAME

    if (hostName === 'app.housefolios.com') {
      genericSendEmail({
        variables: {
          msg: {
            to: [{ addr: marketplace.contactEmail }],
            subject: `You have been contacted`,
            text: `${name} has sent a message from your marketplace ${marketplace.url}. They can be contacted at ${email} or ${phone}. Their message is: \n${message}`,
            html: `${name} has sent a message from your marketplace ${marketplace.url}. They can be contacted at ${email} or ${phone}. Their message is: \n${message}`,
          },
        },
      }).then(() => {
        enqueueSnackbar(`Your Email was Sent`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
      })
    } else {
      genericSendEmail({
        variables: {
          msg: {
            to: [{ addr: 'daniel@housefolios.com' }],
            subject: `Dev/Test You have been contacted `,
            text: `${name} has sent a message from your marketplace ${marketplace.url}. They can be contacted at ${email} or ${phone}. Their message is: \n${message}`,
            html: `${name} has sent a message from your marketplace ${marketplace.url}. They can be contacted at ${email} or ${phone}. Their message is: \n${message}`,
          },
        },
      }).then(() => {
        enqueueSnackbar(`Your Email was Sent`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
      })
    }
  }

  return {
    // Data
    properties: unfilteredProperties,
    displayProperties,
    marketplaceVideoURL,
    affiliate,
    urlAffiliate,
    orgName,

    // Form state
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    message,
    setMessage,

    // Loading states
    loadingMarketPlaceData,
    marketPlaceError,

    // Event handlers
    sendEmail,
    contactUs,
  }
}
